import React, { useContext, useEffect, useRef, useState } from 'react';

import styles from './ListaStatusFaturaUsina.module.css';
import styles_global from '../css/global.module.css';
import styles_content from '../css/main-content.module.css';

import { useHttpClient } from '../hooks/useHttpClient';
import { useNotificacoes } from '../hooks/useNotificacoes';
import { UserContext } from '../../UserContext';

import Pagination from '../pagination/Pagination';
import { useLocation } from 'react-router-dom';

// Mapa de rótulos de status (fallback caso o backend não mande statusDescricao)
const STATUS_LABELS = {
    IMPORTADA: 'Importada',
    IMPORTADO: 'Importada',
    BAIXADA_NAO_IMPORTADA: 'Baixada, mas ainda não importada',
    UC_NAO_CADASTRADA: 'Baixada, mas UC não cadastrada no sistema',
    ERRO_CIP: 'Erro ao ler CIP (Taxa de Iluminação)',
    ERRO_PARSE: 'Erro ao ler a fatura',
    ERRO_NEGOCIO: 'Erro de negócio ao gerar consumo',
    DUPLICADA: 'Duplicada (já existe fatura para a competência)',
    NAO_BAIXADA: 'Nenhuma fatura baixada para o mês',
    PENDENTE: 'Pendente',
    PROCESSADO_OK: 'Importada com sucesso',
    RECEBIDO: 'Baixada, aguardando processamento',
};

// Classes CSS por código de status
const STATUS_CLASS = {
    IMPORTADA: 'statusImportada',
    IMPORTADO: 'statusImportada',
    PROCESSADO_OK: 'statusImportada',

    BAIXADA_NAO_IMPORTADA: 'statusBaixada',
    RECEBIDO: 'statusBaixada',

    UC_NAO_CADASTRADA: 'statusUcNaoCadastrada',

    ERRO_CIP: 'statusErro',
    ERRO_PARSE: 'statusErro',
    ERRO_NEGOCIO: 'statusErro',

    DUPLICADA: 'statusDuplicada',
    NAO_BAIXADA: 'statusNaoBaixada',
    PENDENTE: 'statusPendente',
};

function getAnoMesAtual() {
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    return `${now.getFullYear()}-${month}`; // yyyy-MM
}

function ListaStatusFaturasUsina() {
    const { user } = useContext(UserContext);
    const { get } = useHttpClient();
    const { exibirErro } = useNotificacoes();
    const location = useLocation();

    // combos
    const [empresas, setEmpresas] = useState([]);
    const [usinas, setUsinas] = useState([]);

    const [filtros, setFiltros] = useState({
        idEmpresa: user?.empresa?.id ? String(user.empresa.id) : '',
        idUsina: '',
        competencia: getAnoMesAtual(),
    });

    const [statusUsina, setStatusUsina] = useState(null);
    const [loading, setLoading] = useState(false);

    // paginação (0-based)
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(0);

    // controle de fluxo do deep-link (👁️)
    const deepLinkRef = useRef({ idUsinaUrl: null, competenciaUrl: null });
    const jaProcessouDeepLink = useRef(false);
    const carregandoDeepLink = useRef(false);

    // 1) ao montar, carrega empresas e prepara deep-link
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        deepLinkRef.current.idUsinaUrl = params.get('idUsina');
        deepLinkRef.current.competenciaUrl = params.get('competencia');

        carregarEmpresas();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // 2) sempre que empresa mudar, carrega usinas daquela empresa
    useEffect(() => {
        if (!filtros.idEmpresa) return;
        carregarUsinas(filtros.idEmpresa);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filtros.idEmpresa]);

    // 3) quando usinas carregarem, se veio deep-link e a usina estiver na lista, seleciona e consulta
    useEffect(() => {
        if (!usinas.length) return;
        if (jaProcessouDeepLink.current) return;

        const { idUsinaUrl, competenciaUrl } = deepLinkRef.current;
        if (!idUsinaUrl) return;

        const existe = usinas.some(u => String(u.id) === String(idUsinaUrl));
        if (!existe) return;

        jaProcessouDeepLink.current = true;

        const comp = competenciaUrl ? String(competenciaUrl) : filtros.competencia;

        setFiltros(prev => ({
            ...prev,
            idUsina: String(idUsinaUrl),
            competencia: comp,
        }));

        consultarStatus(String(idUsinaUrl), comp, 0);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [usinas]);

    const carregarEmpresas = async () => {
        try {
            const empresaIdUsuario = user?.empresa?.id;
            if (!empresaIdUsuario) return;

            const lista = (await get(`/hello-sol/empresas/lista/${empresaIdUsuario}`)) || [];
            const empresasLista = Array.isArray(lista) ? lista : [];
            setEmpresas(empresasLista);

            // default empresa (user ou primeira)
            const defaultEmpresaId = String(user?.empresa?.id || (empresasLista[0]?.id ?? ''));
            setFiltros(prev => ({
                ...prev,
                idEmpresa: prev.idEmpresa || defaultEmpresaId,
            }));

            // ✅ PERFEITO PERFEITO:
            // se veio idUsina na URL, descobre a empresa da usina e seta a empresa correta
            const { idUsinaUrl, competenciaUrl } = deepLinkRef.current;
            if (idUsinaUrl && !jaProcessouDeepLink.current && !carregandoDeepLink.current) {
                carregandoDeepLink.current = true;

                try {
                    const usina = await get(`/hello-sol/usinas/${idUsinaUrl}`);
                    const empresaDaUsinaId = usina?.empresaGestora?.id;

                    if (empresaDaUsinaId) {
                        const existeEmpresa = empresasLista.some(e => String(e.id) === String(empresaDaUsinaId));
                        if (existeEmpresa) {
                            // muda empresa -> isso vai disparar carregarUsinas(empresaDaUsinaId)
                            setFiltros(prev => ({
                                ...prev,
                                idEmpresa: String(empresaDaUsinaId),
                                competencia: competenciaUrl ? String(competenciaUrl) : prev.competencia,
                                idUsina: '',
                            }));
                            // não consulta aqui; consulta quando a lista de usinas carregar (useEffect acima)
                        }
                    }
                } catch (e) {
                    // se falhar, segue com empresa default mesmo
                    console.warn('Não foi possível determinar empresa da usina pelo deep-link.', e);
                } finally {
                    carregandoDeepLink.current = false;
                }
            }
        } catch (error) {
            console.error(error);
            exibirErro('Falha ao carregar empresas.');
            setEmpresas([]);
        }
    };

    const carregarUsinas = async (idEmpresa) => {
        try {
            const data = await get(`/hello-sol/usinas/buscar?idEmpresa=${idEmpresa}`);
            const lista = Array.isArray(data?.content) ? data.content : (Array.isArray(data) ? data : []);
            setUsinas(lista);

            // ao trocar empresa manualmente, limpa seleção/resultado
            setStatusUsina(null);
            setCurrentPage(0);
            setTotalPages(0);

            // se a troca foi manual, limpa idUsina selecionada
            // (se for deep-link, ele será preenchido no useEffect(usinas))
            setFiltros(prev => ({
                ...prev,
                idUsina: '',
            }));
        } catch (error) {
            console.error(error);
            exibirErro('Falha ao carregar usinas.');
            setUsinas([]);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === 'idEmpresa') {
            // troca manual: permite novo deep-link no futuro se quiser
            jaProcessouDeepLink.current = true; // não tenta reaplicar deep-link antigo após troca manual
            setFiltros(prev => ({
                ...prev,
                idEmpresa: value,
                idUsina: '',
            }));
            return;
        }

        setFiltros(prev => ({
            ...prev,
            [name]: value,
        }));
    };

    const consultarStatus = async (idUsina, competencia, page = 0) => {
        try {
            setLoading(true);

            const url = `/hello-sol/faturas-status/usina/${idUsina}?competencia=${competencia}`;
            const data = await get(url);

            const unidades = Array.isArray(data?.unidades) ? data.unidades : [];
            const total = Math.ceil(unidades.length / pageSize);

            setStatusUsina(data);
            setTotalPages(total);
            setCurrentPage(page);
        } catch (error) {
            console.error(error);
            exibirErro('Falha ao consultar o status das faturas.');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!filtros.idEmpresa) {
            exibirErro('Selecione a empresa.');
            return;
        }
        if (!filtros.idUsina) {
            exibirErro('Selecione a usina.');
            return;
        }
        if (!filtros.competencia) {
            exibirErro('Informe a competência.');
            return;
        }

        setCurrentPage(0);
        await consultarStatus(filtros.idUsina, filtros.competencia, 0);
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 0 && newPage < totalPages) {
            setCurrentPage(newPage);
        }
    };

    const getStatusLabel = (uc) => {
        if (uc.statusDescricao) return uc.statusDescricao;
        if (uc.statusCodigo && STATUS_LABELS[uc.statusCodigo]) return STATUS_LABELS[uc.statusCodigo];
        return uc.statusCodigo || '—';
    };

    const getStatusClassName = (uc) => {
        const code = uc.statusCodigo;
        const cssKey = code && STATUS_CLASS[code] ? STATUS_CLASS[code] : 'statusDefault';
        return `${styles.statusBadge} ${styles[cssKey]}`;
    };

    const unidades = statusUsina?.unidades || [];
    const startIndex = currentPage * pageSize;
    const endIndex = startIndex + pageSize;
    const unidadesPaginadas = unidades.slice(startIndex, endIndex);

    return (
        <div className={styles_global.container}>
            <h2>Status das Faturas por Usina</h2>

            <form className={styles.filterForm} onSubmit={handleSubmit}>
                {/* Empresa */}
                <div className={`${styles_content.formField} ${styles.empresaField}`}>
                    <label>Empresa</label>
                    <select
                        name="idEmpresa"
                        value={filtros.idEmpresa}
                        onChange={handleChange}
                    >
                        <option value="">Selecione a Empresa</option>
                        {empresas.map(emp => (
                            <option key={emp.id} value={emp.id}>
                                {emp.nome}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Usina */}
                <div className={`${styles_content.formField} ${styles.usinaField}`}>
                    <label>Usina</label>
                    <select
                        name="idUsina"
                        value={filtros.idUsina}
                        onChange={handleChange}
                        disabled={!filtros.idEmpresa}
                    >
                        <option value="">Selecione a Usina</option>
                        {usinas.map(usina => (
                            <option key={usina.id} value={usina.id}>
                                {usina.nome}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Competência */}
                <div className={`${styles_content.formField} ${styles.competenciaField}`}>
                    <label>Competência</label>
                    <input
                        type="month"
                        name="competencia"
                        value={filtros.competencia}
                        onChange={handleChange}
                    />
                </div>

                <div className={styles.botoesAbaixo}>
                    <button type="submit" disabled={loading}>
                        {loading ? 'Consultando...' : 'Consultar'}
                    </button>
                </div>
            </form>

            {loading && <p className={styles_global.loading}>Carregando...</p>}

            {!loading && statusUsina && unidades.length === 0 && (
                <p className={styles_global.noData}>
                    Nenhuma unidade consumidora encontrada para a usina/competência selecionadas.
                </p>
            )}

            {!loading && unidades.length > 0 && (
                <div className={styles.tableContainer}>
                    <table className={styles.table}>
                        <thead>
                        <tr>
                            <th>USINA</th>
                            <th>CLIENTE</th>
                            <th>CÓD. UC</th>
                            <th>NOME UC</th>
                            <th>STATUS</th>
                        </tr>
                        </thead>
                        <tbody>
                        {unidadesPaginadas.map((uc) => (
                            <tr key={uc.ucId ?? uc.codigoUc}>
                                <td>{statusUsina?.nomeUsina}</td>
                                <td>{uc.clienteNome}</td>
                                <td>{uc.codigoUc}</td>
                                <td>{uc.nomeUnidade}</td>
                                <td>
                                    <span className={getStatusClassName(uc)}>
                                        {getStatusLabel(uc)}
                                    </span>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>

                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                    />
                </div>
            )}

            {!loading && !statusUsina && (
                <p className={styles_global.noData}>
                    Selecione a empresa, a usina, informe a competência e clique em <b>Consultar</b>.
                </p>
            )}
        </div>
    );
}

export default ListaStatusFaturasUsina;
